// frontend/src/features/admin/components/AssignClientsModal.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FilterListIcon from '@mui/icons-material/FilterList';

// Importações do services/usersAPI
import { User, Cliente, getClientsByVendor, getAllUsers, transferClientes } from '../../user/services/usersAPI';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import styles from './AssignClientsModal.module.css';

interface SearchableVendorSelectProps {
  label: string;
  value: number | '';
  onChange: (value: number | '') => void;
  options: User[];
  placeholder: string;
  disabled?: boolean;
  excludeValue?: number | '';
}

const SearchableVendorSelect: React.FC<SearchableVendorSelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  excludeValue
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = useMemo(() => {
    return options.find(opt => Number(opt.ID_VENDEDOR) === Number(value)) || null;
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    let list = options;
    if (excludeValue) {
      list = list.filter(opt => Number(opt.ID_VENDEDOR) !== Number(excludeValue));
    }
    if (!search.trim()) return list;

    const lower = search.toLowerCase();
    return list.filter(opt => {
      const name = (opt.NOME || opt.USUARIO || '').toLowerCase();
      const base = String(opt.BASE || '').toLowerCase();
      return name.includes(lower) || base.includes(lower);
    });
  }, [options, search, excludeValue]);

  return (
    <div className={styles.dropdownContainer} ref={containerRef}>
      <label className={styles.selectLabel}>{label}</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setIsOpen(!isOpen);
          setSearch('');
        }}
        className={`${styles.dropdownTrigger} ${disabled ? styles.dropdownTriggerDisabled : ''}`}
      >
        <span>
          {selectedOption
            ? `${selectedOption.NOME || selectedOption.USUARIO} (Base: ${selectedOption.BASE || 'N/A'})`
            : placeholder}
        </span>
        <span className={styles.dropdownArrow}>▼</span>
      </button>

      {isOpen && (
        <div className={styles.dropdownMenu}>
          <input
            type="text"
            className={styles.dropdownSearchInput}
            placeholder="Pesquisar vendedor ou base..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className={styles.dropdownList}>
            {filteredOptions.length === 0 ? (
              <div className={styles.dropdownNoResults}>Nenhum vendedor encontrado</div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = Number(opt.ID_VENDEDOR) === Number(value);
                return (
                  <button
                    key={opt.ID_USUARIO}
                    type="button"
                    onClick={() => {
                      onChange(opt.ID_VENDEDOR ? Number(opt.ID_VENDEDOR) : '');
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={`${styles.dropdownOption} ${isSelected ? styles.dropdownOptionSelected : ''}`}
                  >
                    {opt.NOME || opt.USUARIO} (Base: {opt.BASE || 'N/A'})
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface AssignClientsModalProps {
  isOpen: boolean;
  onClose: () => void;
  users?: User[];
}

const AssignClientsModal: React.FC<AssignClientsModalProps> = ({ isOpen, onClose, users: usersProp }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [originClients, setOriginClients] = useState<Cliente[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  
  const [originVendorId, setOriginVendorId] = useState<number | ''>('');
  const [destinationVendorId, setDestinationVendorId] = useState<number | ''>('');
  const [selectedBaseFilter, setSelectedBaseFilter] = useState<string>('');
  
  const [destinationClientsCount, setDestinationClientsCount] = useState<number | null>(null);
  const [loadingDestCount, setLoadingDestCount] = useState(false);
  
  const [selectedClientIdsToTrade, setSelectedClientIdsToTrade] = useState<Set<number>>(new Set());
  const [recentlyTransferred, setRecentlyTransferred] = useState<{ client: Cliente; originalVendorId: number }[]>([]);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTerm, setFilterTerm] = useState('');
  const [limit, setLimit] = useState(30);

  const [dialog, setDialog] = useState<{
    type: 'success' | 'error' | 'confirm_close';
    title: string;
    message: string;
    onConfirm?: () => void;
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadData();
    } else {
      // Resetar estados ao fechar
      setAllUsers([]);
      setOriginClients([]);
      setOriginVendorId('');
      setDestinationVendorId('');
      setSelectedBaseFilter('');
      setDestinationClientsCount(null);
      setSelectedClientIdsToTrade(new Set());
      setRecentlyTransferred([]);
      setSearchTerm('');
      setFilterTerm('');
      setLimit(30);
      setDialog(null);
    }
  }, [isOpen]);

  const loadData = async () => {
    if (usersProp && usersProp.length > 0) return;
    setLoading(true);
    try {
      const data = await getAllUsers();
      setAllUsers(data);
    } catch (error: any) {
      console.error('Erro ao carregar usuários:', error);
      setDialog({
        type: 'error',
        title: 'Erro de conexão',
        message: 'Ocorreu um erro ao carregar os dados. Tente novamente.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOriginChange = async (vendorId: number | '') => {
    setOriginVendorId(vendorId);
    setSelectedBaseFilter('');
    setSelectedClientIdsToTrade(new Set());
    setRecentlyTransferred([]);
    setSearchTerm('');
    setFilterTerm('');
    setLimit(30);
    
    // Se o vendedor de destino for o mesmo, limpa
    if (destinationVendorId === vendorId) {
      setDestinationVendorId('');
      setDestinationClientsCount(null);
    }

    if (vendorId) {
      setLoadingClients(true);
      try {
        const clientsData = await getClientsByVendor(String(vendorId));
        const sortedClients = [...clientsData].sort((a, b) =>
          (a.NOME || '').localeCompare(b.NOME || '')
        );
        setOriginClients(sortedClients);
      } catch (error) {
        console.error('Erro ao carregar clientes do vendedor de origem:', error);
        setOriginClients([]);
      } finally {
        setLoadingClients(false);
      }
    } else {
      setOriginClients([]);
    }
  };

  const handleDestinationChange = async (vendorId: number | '') => {
    setDestinationVendorId(vendorId);
    setRecentlyTransferred([]);
    setDestinationClientsCount(null);

    if (vendorId) {
      setLoadingDestCount(true);
      try {
        const clientsData = await getClientsByVendor(String(vendorId));
        setDestinationClientsCount(clientsData.length);
      } catch (error) {
        console.error('Erro ao carregar quantidade de clientes do destino:', error);
      } finally {
        setLoadingDestCount(false);
      }
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setFilterTerm(searchTerm.toLowerCase().trim());
    }
  };

  // Lista de usuários a utilizar (se vier via prop, usa ela, senão carrega localmente)
  const listUsers = useMemo(() => {
    return usersProp || allUsers;
  }, [usersProp, allUsers]);

  // Filtrar usuários comerciais (vendedores/representantes)
  const commercialUsers = useMemo(() => {
    return listUsers.filter(u =>
      u.ID_VENDEDOR &&
      String(u.ID_VENDEDOR).trim() !== '' &&
      String(u.ID_VENDEDOR).trim() !== '0'
    ).sort((a, b) => (a.NOME || '').localeCompare(b.NOME || ''));
  }, [listUsers]);

  // Vendedor de origem selecionado
  const selectedOriginVendor = useMemo(() => {
    if (!originVendorId) return null;
    return commercialUsers.find(u => Number(u.ID_VENDEDOR) === Number(originVendorId)) || null;
  }, [commercialUsers, originVendorId]);

  // Vendedor de destino selecionado
  const selectedDestinationVendor = useMemo(() => {
    if (!destinationVendorId) return null;
    return commercialUsers.find(u => Number(u.ID_VENDEDOR) === Number(destinationVendorId)) || null;
  }, [commercialUsers, destinationVendorId]);

  // Obter bases presentes na carteira do vendedor selecionado
  const originVendorBases = useMemo(() => {
    if (!originVendorId) return [];
    const bases = new Set<string>();
    originClients.forEach(c => {
      if (c.BASE !== undefined && c.BASE !== null) {
        bases.add(String(c.BASE).trim());
      }
    });
    return Array.from(bases).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [originClients, originVendorId]);

  // Filtrar clientes da base de origem do vendedor selecionado
  const filteredClients = useMemo(() => {
    if (!originVendorId) return [];
    
    let clients = [...originClients];

    if (selectedBaseFilter) {
      clients = clients.filter(c => String(c.BASE).trim() === selectedBaseFilter);
    }

    if (filterTerm) {
      clients = clients.filter(c =>
        (c.NOME || '').toLowerCase().includes(filterTerm) ||
        (c.FANTASIA || '').toLowerCase().includes(filterTerm) ||
        c.ID.toString().includes(filterTerm)
      );
    }

    return clients;
  }, [originClients, selectedBaseFilter, filterTerm]);

  const clientsToDisplay = useMemo(() => {
    return filteredClients.slice(0, limit);
  }, [filteredClients, limit]);

  const toggleClientSelection = (clientId: number) => {
    setSelectedClientIdsToTrade(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  const areAllSelected = useMemo(() => {
    if (clientsToDisplay.length === 0) return false;
    return clientsToDisplay.every(c => selectedClientIdsToTrade.has(c.ID));
  }, [clientsToDisplay, selectedClientIdsToTrade]);

  const handleSelectAll = () => {
    if (areAllSelected) {
      setSelectedClientIdsToTrade(prev => {
        const next = new Set(prev);
        clientsToDisplay.forEach(c => next.delete(c.ID));
        return next;
      });
    } else {
      setSelectedClientIdsToTrade(prev => {
        const next = new Set(prev);
        clientsToDisplay.forEach(c => next.add(c.ID));
        return next;
      });
    }
  };

  const selectedClientsPreview = useMemo(() => {
    return originClients.filter(c => selectedClientIdsToTrade.has(c.ID));
  }, [originClients, selectedClientIdsToTrade]);

  const handleCloseAttempt = () => {
    if (selectedClientIdsToTrade.size > 0) {
      setDialog({
        type: 'confirm_close',
        title: 'Cancelar Operação',
        message: 'Você tem clientes selecionados para transferência. Se fechar agora, perderá a seleção.',
        onConfirm: () => {
          setDialog(null);
          onClose();
        }
      });
    } else {
      onClose();
    }
  };

  // Efetuar a Transferência no Backend
  const handleTransfer = async () => {
    if (!originVendorId || !destinationVendorId || selectedClientIdsToTrade.size === 0) return;
    
    setSaving(true);
    
    try {
      const clientIdsArray = Array.from(selectedClientIdsToTrade);
      await transferClientes(originVendorId, destinationVendorId, clientIdsArray);
      
      const transferredClientsList = originClients.filter(c => selectedClientIdsToTrade.has(c.ID));
      const updatedOriginClients = originClients.filter(c => !selectedClientIdsToTrade.has(c.ID));
      
      setOriginClients(updatedOriginClients);
      
      const count = selectedClientIdsToTrade.size;
      const originName = selectedOriginVendor?.NOME || 'Vendedor Origem';
      const destName = selectedDestinationVendor?.NOME || 'Vendedor Destino';

      // Salva os clientes transferidos na sessão de hoje
      const newlyTransferred = transferredClientsList.map(c => ({
        client: c,
        originalVendorId: Number(originVendorId)
      }));
      setRecentlyTransferred(prev => [...newlyTransferred, ...prev]);

      // Atualiza a contagem se ela estiver carregada na tela
      if (destinationClientsCount !== null) {
        setDestinationClientsCount(prev => (prev || 0) + count);
      }

      setDialog({
        type: 'success',
        title: 'Transferência Concluída!',
        message: `Sucesso! ${count} cliente(s) transferido(s) de "${originName}" para "${destName}". Você pode desfazer a ação na coluna da direita se necessário.`,
        onConfirm: () => {
          setDialog(null);
          setSelectedClientIdsToTrade(new Set());
        }
      });
    } catch (err) {
      console.error('Erro ao transferir clientes:', err);
      setDialog({
        type: 'error',
        title: 'Erro ao transferir',
        message: 'Não foi possível completar a transferência dos clientes no banco de dados.'
      });
    } finally {
      setSaving(false);
    }
  };

  // Desfazer Transferência no Backend
  const handleReturnClient = async (clientId: number, originalVendorId: number) => {
    const itemToRestore = recentlyTransferred.find(item => item.client.ID === clientId);
    if (!itemToRestore) return;

    setSaving(true);
    try {
      // Devolve para a origem no backend
      await transferClientes(
        destinationVendorId, // vendedor que havia recebido
        originalVendorId,    // vendedor de origem original
        [clientId]
      );

      // Coloca de volta na lista de clientes de origem
      setOriginClients(prev => [...prev, itemToRestore.client].sort((a, b) =>
        (a.NOME || '').localeCompare(b.NOME || '')
      ));

      // Remove do recentemente transferidos
      setRecentlyTransferred(prev => prev.filter(item => item.client.ID !== clientId));

      // Atualiza a contagem de destino se carregada
      if (destinationClientsCount !== null) {
        setDestinationClientsCount(prev => Math.max(0, (prev || 0) - 1));
      }
    } catch (err) {
      console.error('Erro ao desfazer transferência:', err);
      setDialog({
        type: 'error',
        title: 'Erro ao desfazer',
        message: 'Não foi possível devolver o cliente para o vendedor de origem no banco de dados.'
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={handleCloseAttempt}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <div className={styles.titleWithIcon}>
              <CompareArrowsIcon className={styles.headerIcon} />
              <h2>Configurar Clientes</h2>
            </div>
            <button onClick={handleCloseAttempt} className={styles.closeButton}>
              <CloseIcon />
            </button>
          </div>

          {loading ? (
            <div className={styles.loadingWrapper}>
              <LoadingSpinner />
              <p>Carregando dados dos vendedores...</p>
            </div>
          ) : (
            <div className={styles.tradeGridContainer}>
              
              {/* COLUNA ESQUERDA: Origem */}
              <div className={styles.tradeColumn}>
                <div className={`${styles.columnHeader} ${styles.originHeader}`}>
                  <span>ORIGEM</span>
                </div>
                
                <div className={styles.columnBody}>
                  <SearchableVendorSelect
                    label="Vendedor Saindo"
                    value={originVendorId}
                    onChange={(val) => handleOriginChange(val)}
                    options={commercialUsers}
                    placeholder="Selecione o Vendedor..."
                  />

                  {selectedOriginVendor ? (
                    <div className={styles.vendorCard}>
                      <div className={`${styles.avatar} ${styles.originAvatar}`}>
                        {selectedOriginVendor.NOME ? selectedOriginVendor.NOME.charAt(0).toUpperCase() : <PersonIcon />}
                      </div>
                      <div className={styles.vendorDetails}>
                        <h3>{selectedOriginVendor.NOME || selectedOriginVendor.USUARIO}</h3>
                        <div className={styles.vendorDetailsRow}>
                          <span>ID: {selectedOriginVendor.ID_VENDEDOR}</span>
                          <span>•</span>
                          <span>Base: {selectedOriginVendor.BASE || 'N/A'}</span>
                        </div>
                      </div>
                      <div className={styles.vendorStats}>
                        <div className={styles.statsValue}>
                          {originClients.length}
                        </div>
                        <div className={styles.statsLabel}>Clientes</div>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.placeholderCard}>
                      <PersonIcon />
                      <p>Selecione o vendedor de origem</p>
                    </div>
                  )}
                </div>
              </div>

              {/* COLUNA CENTRAL: Clientes para Troca */}
              <div className={`${styles.tradeColumn} ${styles.middleColumn}`}>
                <div className={styles.columnHeader}>
                  <span>CLIENTES DA CARTEIRA</span>
                  {selectedClientIdsToTrade.size > 0 && (
                    <span className={styles.selectedCountBadge}>
                      {selectedClientIdsToTrade.size} selecionado(s)
                    </span>
                  )}
                </div>

                <div className={styles.columnBody}>
                  {originVendorId ? (
                    <>
                      {loadingClients ? (
                        <div className={styles.loadingWrapper} style={{ padding: '40px 12px' }}>
                          <LoadingSpinner />
                          <p>Carregando carteira de clientes...</p>
                        </div>
                      ) : (
                        <>
                          <div className={styles.middleFiltersRow}>
                            <div className={styles.middleSearchBox}>
                              <SearchIcon className={styles.searchIcon} />
                              <input
                                type="text"
                                placeholder="Buscar nome ou ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                className={styles.searchInput}
                              />
                            </div>

                            {originVendorBases.length > 1 && (
                              <div className={styles.baseFilterBox}>
                                <FilterListIcon className={styles.filterIcon} />
                                <select
                                  value={selectedBaseFilter}
                                  onChange={(e) => {
                                    setSelectedBaseFilter(e.target.value);
                                    setLimit(30);
                                  }}
                                  className={styles.baseFilterSelect}
                                >
                                  <option value="">Todas as Bases</option>
                                  {originVendorBases.map(b => (
                                    <option key={b} value={b}>Base {b}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>

                          <div className={styles.middleListContainer}>
                            <table className={styles.clientTable}>
                              <thead>
                                <tr>
                                  <th>
                                    <input
                                      type="checkbox"
                                      checked={areAllSelected}
                                      onChange={handleSelectAll}
                                      className={styles.checkbox}
                                    />
                                  </th>
                                  <th>ID / Nome</th>
                                </tr>
                              </thead>
                              <tbody>
                                {clientsToDisplay.length === 0 ? (
                                  <tr>
                                    <td colSpan={2} className={styles.emptyState}>
                                      Nenhum cliente correspondente.
                                    </td>
                                  </tr>
                                ) : (
                                  clientsToDisplay.map(client => {
                                    const isSelected = selectedClientIdsToTrade.has(client.ID);
                                    return (
                                      <tr 
                                        key={client.ID} 
                                        onClick={() => toggleClientSelection(client.ID)}
                                        className={isSelected ? styles.selectedRow : ''}
                                      >
                                        <td onClick={(e) => e.stopPropagation()}>
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleClientSelection(client.ID)}
                                            className={styles.checkbox}
                                          />
                                        </td>
                                        <td>
                                          <div className={styles.clientCellLayout}>
                                            <div className={styles.clientTextData}>
                                              <strong>{client.NOME || 'Cliente sem nome'}</strong>
                                              <span>ID: {client.ID} | {client.FANTASIA}</span>
                                            </div>
                                            {client.BASE && (
                                              <span className={styles.clientBaseBadge}>
                                                B{client.BASE}
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                            
                            {filteredClients.length > limit && (
                              <button 
                                type="button" 
                                onClick={() => setLimit(prev => prev + 30)} 
                                className={styles.loadMoreBtn}
                              >
                                Carregar Mais (+30)
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className={styles.placeholderCard}>
                      <CompareArrowsIcon />
                      <p>Selecione o vendedor de origem para ver os clientes</p>
                    </div>
                  )}
                </div>
              </div>

              {/* COLUNA DIREITA: Destino */}
              <div className={styles.tradeColumn}>
                <div className={`${styles.columnHeader} ${styles.destinationHeader}`}>
                  <span>DESTINO</span>
                </div>

                <div className={styles.columnBody}>
                  <SearchableVendorSelect
                    label="Vendedor Recebendo"
                    disabled={!originVendorId}
                    value={destinationVendorId}
                    onChange={(val) => handleDestinationChange(val)}
                    options={commercialUsers}
                    excludeValue={originVendorId}
                    placeholder="Selecione o Vendedor..."
                  />

                  {selectedDestinationVendor ? (
                    <>
                      <div className={styles.vendorCard}>
                        <div className={`${styles.avatar} ${styles.destinationAvatar}`}>
                          {selectedDestinationVendor.NOME ? selectedDestinationVendor.NOME.charAt(0).toUpperCase() : <PersonIcon />}
                        </div>
                        <div className={styles.vendorDetails}>
                          <h3>{selectedDestinationVendor.NOME || selectedDestinationVendor.USUARIO}</h3>
                          <div className={styles.vendorDetailsRow}>
                            <span>ID: {selectedDestinationVendor.ID_VENDEDOR}</span>
                            <span>•</span>
                            <span>Base: {selectedDestinationVendor.BASE || 'N/A'}</span>
                          </div>
                        </div>
                        
                        <div className={styles.vendorStats}>
                          <div className={styles.statsValue}>
                            {loadingDestCount ? '...' : destinationClientsCount ?? 0}
                          </div>
                          <div className={styles.statsLabel}>
                            Clientes
                          </div>
                        </div>
                      </div>

                      {/* Fila de Transição de Clientes */}
                      {selectedClientIdsToTrade.size > 0 && (
                        <div className={styles.transitionQueueBox}>
                          <h4>Fila de Clientes a Transferir</h4>
                          <div className={styles.queueList}>
                            {selectedClientsPreview.map(c => (
                              <div key={c.ID} className={styles.queueItem}>
                                <ArrowForwardIcon className={styles.queueArrow} />
                                <div className={styles.queueItemInfo}>
                                  <strong>{c.NOME}</strong>
                                  <span>ID: {c.ID} {c.BASE ? `| Base ${c.BASE}` : ''}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Clientes Transferidos nesta Sessão */}
                      {recentlyTransferred.length > 0 && (
                        <div className={styles.transitionQueueBox}>
                          <h4 style={{ color: 'var(--color-primary, #2563eb)' }}>Transferidos nesta Sessão ({recentlyTransferred.length})</h4>
                          <div className={styles.queueList}>
                            {recentlyTransferred.map(item => (
                              <div key={item.client.ID} className={styles.queueItem} style={{ justifyContent: 'space-between' }}>
                                <div className={styles.queueItemInfo}>
                                  <strong>{item.client.NOME}</strong>
                                  <span>ID: {item.client.ID} {item.client.BASE ? `| Base ${item.client.BASE}` : ''}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleReturnClient(item.client.ID, item.originalVendorId)}
                                  className={styles.dialogCancelBtn}
                                  style={{ padding: '2px 8px', fontSize: '11px', height: '24px', margin: 0 }}
                                  title="Devolver para a carteira de origem"
                                >
                                  Desfazer
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className={styles.placeholderCard}>
                      <PersonIcon />
                      <p>Selecione o vendedor de destino</p>
                    </div>
                  )}
                </div>
              </div>

            </div>
          )}

          <div className={styles.formActions}>
            <button type="button" onClick={handleCloseAttempt} className={styles.cancelButton}>
               Cancelar
            </button>
            <button 
              type="button" 
              onClick={handleTransfer} 
              disabled={saving || !originVendorId || !destinationVendorId || selectedClientIdsToTrade.size === 0} 
              className={styles.saveButton}
            >
              <CompareArrowsIcon />
              {saving ? 'Transferindo...' : `Confirmar Transferência (${selectedClientIdsToTrade.size})`}
            </button>
          </div>
        </div>
      </div>

      {/* Modal Customizado para Diálogos */}
      {dialog && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              {dialog.type === 'success' && <CheckCircleIcon className={styles.successIcon} />}
              {dialog.type === 'error' && <ErrorIcon className={styles.errorIcon} />}
              {dialog.type === 'confirm_close' && <HelpOutlineIcon className={styles.warningIcon} />}
              <h3>{dialog.title}</h3>
            </div>
            
            <div className={styles.dialogBody}>
              <p>{dialog.message}</p>
            </div>
            
            <div className={styles.dialogFooter}>
              {dialog.type === 'confirm_close' ? (
                <>
                  <button 
                    type="button" 
                    onClick={() => setDialog(null)} 
                    className={styles.dialogCancelBtn}
                  >
                    Voltar à Tela
                  </button>
                  <button 
                    type="button" 
                    onClick={dialog.onConfirm} 
                    className={styles.dialogConfirmBtn}
                  >
                    Sair e Descartar
                  </button>
                </>
              ) : (
                <button 
                  type="button" 
                  onClick={dialog.onConfirm || (() => setDialog(null))} 
                  className={styles.dialogOkBtn}
                >
                  Entendido
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssignClientsModal;